import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  type ReactNode,
} from 'react';
import {
  initialRoomClientState,
  roomReducer,
  type RoomClientState,
} from './roomReducer';
import {
  RoomSocketClient,
  clearHostSession,
  clearStoredSession,
  loadHostSession,
  loadStoredSession,
  saveHostSession,
  saveStoredSession,
} from './RoomSocketClient';
import type { RoomState } from '../types/room.types';
import type { GameMode, SubjectTag } from '../types/gameMode.types';
import type { RoomPhase, RoutingMode } from '../types/room.types';
import type { ActivityPayloadMap } from '../types/activity.types';
import { getActivityPlugin } from '../../activities/registry';
import { createActivityRuntime, validateActivityPayload } from '../../logic/pluginRunner';
import type { StudentExamSlot } from '../types/payloads/evaluacion.types';
import { joinScheduledExam } from './examApi';

interface SetActivityOptions {
  templateId?: string;
  subjectTag?: SubjectTag;
  variant?: string;
}

interface RoomContextValue {
  state: RoomClientState;
  client: RoomSocketClient;
  createRoomAsHost: (hostId: string, title?: string) => Promise<void>;
  joinRoomAsStudent: (params: {
    joinCode: string;
    displayName: string;
    studentId?: string;
  }) => Promise<void>;
  joinExamByAccessCode: (params: {
    accessCode: string;
    displayName: string;
    studentId?: string;
  }) => Promise<void>;
  rejoinStoredSession: () => Promise<void>;
  rejoinAsHost: () => Promise<void>;
  setPhase: (phase: RoomPhase) => Promise<void>;
  setActivity: <M extends GameMode>(
    mode: M,
    payload: ActivityPayloadMap[M],
    options?: SetActivityOptions,
  ) => Promise<void>;
  setRouting: (mode: RoutingMode, teamCount?: number) => Promise<void>;
  endRoom: () => Promise<void>;
  joinAsViewer: (joinCode: string) => Promise<void>;
  dispatchActivityAction: (action: { type: string; payload?: unknown }) => Promise<void>;
  submitStudentInput: (action: string, data: unknown) => Promise<void>;
  launchExam: (exam: unknown) => Promise<void>;
  advanceExamBlock: () => Promise<void>;
  endExam: () => Promise<void>;
  confirmExamReview: (participantId: string, blockId: string) => Promise<void>;
  unlockParticipant: (participantId: string) => Promise<void>;
  reportExamBreach: (reason: 'visibility' | 'blur' | 'fullscreen_exit') => Promise<void>;
}

const RoomContext = createContext<RoomContextValue | null>(null);

export function RoomProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(roomReducer, initialRoomClientState);
  const client = useMemo(() => new RoomSocketClient(dispatch), []);

  useEffect(() => {
    client.connect();
    return () => client.disconnect();
  }, [client]);

  const applyServerState = useCallback((roomState: RoomState | undefined) => {
    if (roomState) dispatch({ type: 'STATE_SYNC', payload: roomState });
  }, []);

  const createRoomAsHost = useCallback(
    async (hostId: string, title?: string) => {
      const created = await client.createRoom(hostId, title);
      dispatch({ type: 'SET_ROLE', payload: { role: 'host', hostId } });
      dispatch({
        type: 'SET_SESSION',
        payload: { roomId: created.roomId, joinCode: created.joinCode },
      });
      dispatch({ type: 'STATE_SYNC', payload: created.state });
      saveHostSession({
        roomId: created.roomId,
        joinCode: created.joinCode,
        hostId,
      });
      client.setRejoinMeta({ role: 'host', roomId: created.roomId, hostId });
      const ack = await client.hostJoinRoom(created.roomId, hostId);
      if (!ack.ok) dispatch({ type: 'SET_ERROR', payload: ack.error || 'host_join_failed' });
      else applyServerState(ack.state);
    },
    [client],
  );

  const rejoinAsHost = useCallback(async () => {
    const stored = loadHostSession();
    if (!stored) return;
    dispatch({ type: 'SET_ROLE', payload: { role: 'host', hostId: stored.hostId } });
    dispatch({
      type: 'SET_SESSION',
      payload: { roomId: stored.roomId, joinCode: stored.joinCode },
    });
    client.setRejoinMeta({
      role: 'host',
      roomId: stored.roomId,
      hostId: stored.hostId,
    });
    try {
      const looked = await client.lookupRoom(stored.joinCode);
      dispatch({ type: 'STATE_SYNC', payload: looked.state });
    } catch {
      dispatch({ type: 'SET_ERROR', payload: 'room_not_found' });
      clearHostSession();
      return;
    }
    const ack = await client.hostJoinRoom(stored.roomId, stored.hostId);
    if (!ack.ok) {
      dispatch({ type: 'SET_ERROR', payload: ack.error || 'host_rejoin_failed' });
      return;
    }
    applyServerState(ack.state);
  }, [client]);

  const completeStudentJoin = useCallback(
    async (joined: {
      roomId: string;
      joinCode: string;
      participantId: string;
      sessionToken: string;
      state: RoomState;
      accessCode?: string;
      sessionKind: 'live' | 'async_exam';
    }) => {
      dispatch({ type: 'SET_ERROR', payload: null });
      dispatch({ type: 'SET_ROLE', payload: { role: 'student' } });
      dispatch({
        type: 'SET_SESSION',
        payload: {
          roomId: joined.roomId,
          joinCode: joined.joinCode,
          participantId: joined.participantId,
          sessionToken: joined.sessionToken,
        },
      });
      saveStoredSession({
        roomId: joined.roomId,
        joinCode: joined.joinCode,
        participantId: joined.participantId,
        sessionToken: joined.sessionToken,
        ...(joined.accessCode ? { accessCode: joined.accessCode } : {}),
        sessionKind: joined.sessionKind,
      });
      dispatch({ type: 'STATE_SYNC', payload: joined.state });
      client.setRejoinMeta({
        role: 'student',
        roomId: joined.roomId,
        participantId: joined.participantId,
        sessionToken: joined.sessionToken,
      });
      const ack = await client.studentJoinRoom({
        roomId: joined.roomId,
        participantId: joined.participantId,
        sessionToken: joined.sessionToken,
      });
      if (!ack.ok) {
        dispatch({ type: 'SET_ERROR', payload: ack.error || 'student_join_failed' });
        return;
      }
      applyServerState(ack.state);
      if (ack.examSlot) {
        dispatch({ type: 'SET_EXAM_SLOT', payload: ack.examSlot as StudentExamSlot });
      }
    },
    [applyServerState, client],
  );

  const joinExamByAccessCode = useCallback(
    async (params: { accessCode: string; displayName: string; studentId?: string }) => {
      try {
        const joined = await joinScheduledExam({
          accessCode: params.accessCode,
          displayName: params.displayName,
          studentId: params.studentId,
          baseUrl: client.getBaseUrl(),
        });
        await completeStudentJoin({
          roomId: joined.roomId,
          joinCode: joined.joinCode,
          participantId: joined.participantId,
          sessionToken: joined.sessionToken,
          state: joined.state,
          accessCode: joined.accessCode,
          sessionKind: 'async_exam',
        });
      } catch (error) {
        const message = error instanceof Error ? error.message : 'exam_join_failed';
        dispatch({ type: 'SET_ERROR', payload: message });
      }
    },
    [client, completeStudentJoin],
  );

  const joinRoomAsStudent = useCallback(
    async (params: { joinCode: string; displayName: string; studentId?: string }) => {
      try {
        const joined = await client.joinRoomByCode(params);
        await completeStudentJoin({
          roomId: joined.roomId,
          joinCode: joined.joinCode,
          participantId: joined.participantId,
          sessionToken: joined.sessionToken,
          state: joined.state,
          sessionKind: 'live',
        });
      } catch (error) {
        const message = error instanceof Error ? error.message : 'join_failed';
        if (message === 'USE_EXAM_JOIN') {
          await joinExamByAccessCode({
            accessCode: params.joinCode,
            displayName: params.displayName,
            studentId: params.studentId,
          });
          return;
        }
        dispatch({ type: 'SET_ERROR', payload: message });
      }
    },
    [client, completeStudentJoin, joinExamByAccessCode],
  );

  const rejoinStoredSession = useCallback(async () => {
    const stored = loadStoredSession();
    if (!stored) return;
    dispatch({ type: 'SET_ROLE', payload: { role: 'student' } });
    dispatch({ type: 'SET_SESSION', payload: stored });
    client.setRejoinMeta({
      role: 'student',
      roomId: stored.roomId,
      participantId: stored.participantId,
      sessionToken: stored.sessionToken,
    });
    try {
      const looked = await client.lookupRoom(stored.joinCode);
      dispatch({ type: 'STATE_SYNC', payload: looked.state });
    } catch {
      clearStoredSession();
      dispatch({ type: 'SET_ERROR', payload: 'room_not_found' });
      return;
    }
    const ack = await client.studentJoinRoom(stored);
    if (!ack.ok) {
      clearStoredSession();
      dispatch({ type: 'SET_ERROR', payload: ack.error || 'rejoin_failed' });
      return;
    }
    applyServerState(ack.state);
    if (ack.examSlot) {
      dispatch({ type: 'SET_EXAM_SLOT', payload: ack.examSlot as StudentExamSlot });
    }
  }, [applyServerState, client]);

  const setPhase = useCallback(
    async (phase: RoomPhase) => {
      if (!state.roomId || !state.hostId) return;
      const ack = await client.hostSetPhase({
        roomId: state.roomId,
        hostId: state.hostId,
        phase,
      });
      if (!ack.ok) dispatch({ type: 'SET_ERROR', payload: ack.error || 'set_phase_failed' });
      else applyServerState(ack.state);
    },
    [applyServerState, client, state.hostId, state.roomId],
  );

  const setActivity = useCallback(
    async <M extends GameMode>(
      mode: M,
      payload: ActivityPayloadMap[M],
      options?: SetActivityOptions,
    ) => {
      if (!state.roomId || !state.hostId) return;

      const validated = validateActivityPayload(mode, payload);
      const routing = state.state?.core.routing.mode ?? 'INDIVIDUAL';
      const runtime = createActivityRuntime(mode, validated, routing);
      const variant =
        options?.variant ??
        (validated && typeof validated === 'object' && 'variant' in validated
          ? String((validated as { variant?: string }).variant)
          : undefined);

      const ack = await client.hostSetActivity({
        roomId: state.roomId,
        hostId: state.hostId,
        mode,
        variant,
        subjectTag: options?.subjectTag,
        templateId: options?.templateId,
        payload: validated,
        runtime,
      });
      if (!ack.ok) dispatch({ type: 'SET_ERROR', payload: ack.error || 'set_activity_failed' });
      else applyServerState(ack.state);
    },
    [applyServerState, client, state.hostId, state.roomId, state.state?.core.routing.mode],
  );

  const setRouting = useCallback(
    async (mode: RoutingMode, teamCount?: number) => {
      if (!state.roomId || !state.hostId) return;
      const ack = await client.hostSetRouting({
        roomId: state.roomId,
        hostId: state.hostId,
        mode,
        teamCount,
      });
      if (!ack.ok) dispatch({ type: 'SET_ERROR', payload: ack.error || 'set_routing_failed' });
    },
    [client, state.hostId, state.roomId],
  );

  const endRoom = useCallback(async () => {
    if (!state.roomId || !state.hostId) return;
    await client.hostEndRoom(state.roomId, state.hostId);
    clearStoredSession();
  }, [client, state.hostId, state.roomId]);

  const joinAsViewer = useCallback(
    async (joinCode: string) => {
      const looked = await client.lookupRoom(joinCode);
      dispatch({ type: 'SET_ROLE', payload: { role: 'viewer' } });
      dispatch({
        type: 'SET_SESSION',
        payload: { roomId: looked.roomId, joinCode: looked.joinCode },
      });
      dispatch({ type: 'STATE_SYNC', payload: looked.state });
      client.setRejoinMeta({ role: 'viewer', roomId: looked.roomId });
      const ack = await client.viewerJoinRoom(looked.roomId);
      if (!ack.ok) dispatch({ type: 'SET_ERROR', payload: ack.error || 'viewer_join_failed' });
      else applyServerState(ack.state);
    },
    [client],
  );

  /** El servidor ejecuta plugin.onHostAction — el cliente solo envía la intención. */
  const dispatchActivityAction = useCallback(
    async (action: { type: string; payload?: unknown }) => {
      if (!state.roomId || !state.hostId || !state.state?.core.activity.mode) return;
      const ack = await client.hostAction({
        roomId: state.roomId,
        hostId: state.hostId,
        action: action.type,
        data: action.payload,
      });
      if (!ack.ok) dispatch({ type: 'SET_ERROR', payload: ack.error || 'host_action_failed' });
      else applyServerState(ack.state);
    },
    [applyServerState, client, state.hostId, state.roomId, state.state?.core.activity.mode],
  );

  /** El servidor ejecuta plugin.onStudentInput — el cliente solo envía la intención. */
  const submitStudentInput = useCallback(
    async (action: string, data: unknown) => {
      if (!state.roomId || !state.participantId) {
        dispatch({ type: 'SET_ERROR', payload: 'session_not_ready' });
        return;
      }

      const examSession = state.state?.examSession;
      const examSlot = state.examSlot;

      if (
        examSession?.status === 'ACTIVE' &&
        examSession.advanceMode === 'AUTONOMOUS' &&
        examSlot &&
        !examSlot.finished
      ) {
        await client.ensureRoomMembership();
        const ack = await client.studentInput({
          roomId: state.roomId,
          participantId: state.participantId,
          sessionToken: state.sessionToken ?? undefined,
          input: {
            gameMode: examSlot.mode,
            action,
            data,
            ts: Date.now(),
          },
        });
        if (!ack.ok) dispatch({ type: 'SET_ERROR', payload: ack.error || 'student_input_failed' });
        else {
          if (ack.state) applyServerState(ack.state);
          if (ack.examSlot) dispatch({ type: 'SET_EXAM_SLOT', payload: ack.examSlot as StudentExamSlot });
          else if (ack.examFinished) dispatch({ type: 'SET_EXAM_SLOT', payload: null });
        }
        return;
      }

      if (!state.state?.core.activity.mode) {
        dispatch({ type: 'SET_ERROR', payload: 'session_not_ready' });
        return;
      }
      await client.ensureRoomMembership();
      const ack = await client.studentInput({
        roomId: state.roomId,
        participantId: state.participantId,
        sessionToken: state.sessionToken ?? undefined,
        input: {
          gameMode: state.state.core.activity.mode,
          action,
          data,
          ts: Date.now(),
        },
      });
      if (!ack.ok) dispatch({ type: 'SET_ERROR', payload: ack.error || 'student_input_failed' });
      else applyServerState(ack.state);
    },
    [applyServerState, client, state],
  );

  const reportExamBreach = useCallback(
    async (reason: 'visibility' | 'blur' | 'fullscreen_exit') => {
      if (!state.roomId || !state.participantId) return;
      await client.studentReportBreach({
        roomId: state.roomId,
        participantId: state.participantId,
        sessionToken: state.sessionToken ?? undefined,
        reason,
      });
    },
    [client, state.participantId, state.roomId, state.sessionToken],
  );

  const launchExam = useCallback(
    async (exam: unknown) => {
      if (!state.roomId || !state.hostId) return;
      const ack = await client.hostLaunchExam({
        roomId: state.roomId,
        hostId: state.hostId,
        exam,
      });
      if (!ack.ok) dispatch({ type: 'SET_ERROR', payload: ack.error || 'launch_exam_failed' });
      else applyServerState(ack.state);
    },
    [applyServerState, client, state.hostId, state.roomId],
  );

  const advanceExamBlock = useCallback(async () => {
    if (!state.roomId || !state.hostId) return;
    const ack = await client.hostAdvanceExamBlock({
      roomId: state.roomId,
      hostId: state.hostId,
    });
    if (!ack.ok) dispatch({ type: 'SET_ERROR', payload: ack.error || 'advance_exam_failed' });
    else applyServerState(ack.state);
  }, [applyServerState, client, state.hostId, state.roomId]);

  const endExam = useCallback(async () => {
    if (!state.roomId || !state.hostId) return;
    const ack = await client.hostEndExam({
      roomId: state.roomId,
      hostId: state.hostId,
    });
    if (!ack.ok) dispatch({ type: 'SET_ERROR', payload: ack.error || 'end_exam_failed' });
    else applyServerState(ack.state);
  }, [applyServerState, client, state.hostId, state.roomId]);

  const confirmExamReview = useCallback(
    async (participantId: string, blockId: string) => {
      if (!state.roomId || !state.hostId) return;
      const ack = await client.hostConfirmExamReview({
        roomId: state.roomId,
        hostId: state.hostId,
        participantId,
        blockId,
      });
      if (!ack.ok) dispatch({ type: 'SET_ERROR', payload: ack.error || 'confirm_review_failed' });
      else applyServerState(ack.state);
    },
    [applyServerState, client, state.hostId, state.roomId],
  );

  const unlockParticipant = useCallback(
    async (participantId: string) => {
      if (!state.roomId || !state.hostId) return;
      const ack = await client.hostUnlockParticipant({
        roomId: state.roomId,
        hostId: state.hostId,
        participantId,
      });
      if (!ack.ok) dispatch({ type: 'SET_ERROR', payload: ack.error || 'unlock_failed' });
    },
    [client, state.hostId, state.roomId],
  );

  const value = useMemo<RoomContextValue>(
    () => ({
      state,
      client,
      createRoomAsHost,
      joinRoomAsStudent,
      joinExamByAccessCode,
      rejoinStoredSession,
      rejoinAsHost,
      setPhase,
      setActivity,
      setRouting,
      endRoom,
      joinAsViewer,
      dispatchActivityAction,
      submitStudentInput,
      launchExam,
      advanceExamBlock,
      endExam,
      confirmExamReview,
      unlockParticipant,
      reportExamBreach,
    }),
    [
      state,
      client,
      createRoomAsHost,
      joinRoomAsStudent,
      joinExamByAccessCode,
      rejoinStoredSession,
      rejoinAsHost,
      setPhase,
      setActivity,
      setRouting,
      endRoom,
      joinAsViewer,
      dispatchActivityAction,
      submitStudentInput,
      launchExam,
      advanceExamBlock,
      endExam,
      confirmExamReview,
      unlockParticipant,
      reportExamBreach,
    ],
  );

  return <RoomContext.Provider value={value}>{children}</RoomContext.Provider>;
}

export function useRoomContext() {
  const ctx = useContext(RoomContext);
  if (!ctx) throw new Error('useRoomContext must be used within RoomProvider');
  return ctx;
}
